import { Dispatch, SetStateAction, useMemo } from 'react';
import { ColumnDef, SortingState, OnChangeFn } from '@tanstack/react-table';
import {
  DataTable,
  Pagination,
  PaginationState,
  TableSkeleton,
} from '@/components';
import { EmptyState, ViewAllDataButton } from './components';
import {
  AnyMedicationDataRecord,
  LabResultDataRecord,
  flattenLabResultPanels,
} from './types';
import type { MedicationDataType } from './types';
import {
  createProblemsColumns,
  createMedicationsColumns,
  createLabResultsColumns,
  createProceduresColumns,
  createAllergiesColumns,
  createVaccinationsColumns,
  createVitalsColumns,
} from './columns';

interface MedicationDataTableProps {
  recordType: MedicationDataType;
  records: AnyMedicationDataRecord[];
  totalCount: number;
  isLoading?: boolean;
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  patientId?: string;
}

export const MedicationDataTable = ({
  recordType,
  records,
  totalCount,
  isLoading = false,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  patientId,
}: MedicationDataTableProps) => {
  const columns = useMemo(() => {
    switch (recordType) {
      case 'problems':
        return createProblemsColumns();
      case 'medications':
        return createMedicationsColumns();
      case 'labResults':
        return createLabResultsColumns();
      case 'procedures':
        return createProceduresColumns();
      case 'allergies':
        return createAllergiesColumns();
      case 'vaccinations':
        return createVaccinationsColumns();
      case 'vitals':
        return createVitalsColumns();
      default:
        return [] as ColumnDef<AnyMedicationDataRecord>[];
    }
  }, [recordType]);

  const tableData = useMemo(() => {
    if (recordType === 'labResults') {
      return flattenLabResultPanels(records as LabResultDataRecord[]);
    }
    return records;
  }, [recordType, records]);

  const grouping = recordType === 'labResults' ? ['labPanel'] : undefined;

  if (isLoading) {
    return (
      <div className="min-w-0 flex-1">
        <TableSkeleton rows={5} columns={columns.length || 5} />
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="min-w-0 flex-1">
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1">
      {recordType === 'medications' && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm font-medium">Focused Medications View</span>
          <ViewAllDataButton patientId={patientId} />
        </div>
      )}
      <DataTable
        columns={columns as ColumnDef<unknown>[]}
        data={tableData as unknown[]}
        sorting={sorting}
        onSortingChange={onSortingChange}
        grouping={grouping}
      />
      <Pagination
        pagination={pagination}
        setPagination={onPaginationChange}
        totalCount={totalCount}
        pageSizeOptions={[10, 20, 50]}
      />
    </div>
  );
};
